import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { subscribeInbox, subscribeSent, markAsRead, deleteMultipleDMs } from '../../services/dmService';
import { getUser } from '../../services/userService';
import type { DirectMessage } from '../../types/message';
import { colors } from '../../styles/colors';
import { DefaultAvatar } from '../../components/DefaultAvatar';

type Tab = 'inbox' | 'sent';

// Cache user gender/profile to avoid repeated fetches
const userCache = new Map<string, { gender: 'male' | 'female' | 'unknown'; profileImageUrl?: string }>();

export default function DMListPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>('inbox');
  const [inboxMessages, setInboxMessages] = useState<DirectMessage[]>([]);
  const [sentMessages, setSentMessages] = useState<DirectMessage[]>([]);
  const [selectedDM, setSelectedDM] = useState<DirectMessage | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!user) return;
    const unsubInbox = subscribeInbox(user.uid, setInboxMessages);
    const unsubSent = subscribeSent(user.uid, setSentMessages);
    return () => { unsubInbox(); unsubSent(); };
  }, [user?.uid]);

  // Fetch gender for users not in cache
  useEffect(() => {
    const allMsgs = [...inboxMessages, ...sentMessages];
    const uidsToFetch = new Set<string>();
    for (const msg of allMsgs) {
      const uid = tab === 'inbox' ? msg.senderId : msg.recipientId;
      if (!userCache.has(uid) && uid !== user?.uid) uidsToFetch.add(uid);
      // Also use senderGender if available
      if (msg.senderGender && !userCache.has(msg.senderId)) {
        userCache.set(msg.senderId, { gender: msg.senderGender, profileImageUrl: msg.senderProfileImageUrl });
      }
    }
    if (uidsToFetch.size === 0) return;
    // Fetch missing users
    Promise.all([...uidsToFetch].map(async (uid) => {
      const u = await getUser(uid);
      if (u) userCache.set(uid, { gender: u.gender, profileImageUrl: u.profileImageUrl });
    })).then(() => forceUpdate((n) => n + 1));
  }, [inboxMessages, sentMessages, tab, user?.uid]);

  const messages = tab === 'inbox' ? inboxMessages : sentMessages;
  const blocked = new Set(user?.blockedUserIds ?? []);

  const handleOpen = async (dm: DirectMessage) => {
    setSelectedDM(dm);
    if (tab === 'inbox' && !dm.isRead) await markAsRead(dm.id);
  };

  const formatTime = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const handleReply = (dm: DirectMessage) => {
    setSelectedDM(null);
    window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: dm.senderId, recipientNickname: dm.senderNickname } }));
  };

  const handleViewProfile = (uid: string, nickname: string, e?: React.MouseEvent) => {
    window.dispatchEvent(new CustomEvent('open-user-menu', { detail: { uid, nickname, x: e?.clientX, y: e?.clientY } }));
  };

  const getUserInfo = (uid: string) => userCache.get(uid) ?? { gender: 'unknown' as const, profileImageUrl: undefined };

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      {/* Tabs + Compose button */}
      <div className="flex items-center" style={{ borderBottom: `1px solid ${colors.border}` }}>
        {(['inbox', 'sent'] as const).map((key) => (
          <button key={key} onClick={() => setTab(key)}
            className="flex-1 py-3 text-center text-sm font-semibold"
            style={{ color: tab === key ? colors.primary : colors.textMuted, borderBottom: `2px solid ${tab === key ? colors.primary : 'transparent'}` }}>
            {t(`dm.${key}`)}
            {key === 'inbox' && inboxMessages.filter((m) => !m.isRead).length > 0 && (
              <span className="ml-1 inline-block rounded-full px-1.5 text-xs text-white" style={{ backgroundColor: colors.error }}>
                {inboxMessages.filter((m) => !m.isRead).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto p-3">
        {messages.length === 0 ? (
          <p className="mt-12 text-center text-sm" style={{ color: colors.textMuted }}>{t('dm.noMessages')}</p>
        ) : (
          messages
            .filter((m) => !blocked.has(tab === 'inbox' ? m.senderId : m.recipientId))
            .map((msg) => {
              const otherUid = tab === 'inbox' ? msg.senderId : msg.recipientId;
              const otherNick = tab === 'inbox' ? msg.senderNickname : msg.recipientNickname;
              const info = getUserInfo(otherUid);
              const gc = info.gender === 'male' ? '#4a9eff' : info.gender === 'female' ? '#ff6b9d' : '#999';
              const avatarUrl = info.profileImageUrl || msg.senderProfileImageUrl;

              return (
                <button key={msg.id} onClick={() => handleOpen(msg)}
                  className="mb-2 flex w-full items-center gap-3 rounded-lg p-3 text-left hover:brightness-110"
                  style={{ backgroundColor: colors.card }}>
                  <button onClick={(e) => { e.stopPropagation(); handleViewProfile(otherUid, otherNick, e); }}
                    className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"
                    style={{ backgroundColor: colors.surface, border: `2px solid ${gc}` }}>
                    {avatarUrl
                      ? <img src={avatarUrl} alt="" className="h-full w-full object-cover rounded-full" />
                      : <DefaultAvatar gender={info.gender} size={22} />}
                  </button>
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold" style={{ color: colors.text }}>{otherNick}</p>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xs" style={{ color: colors.textMuted }}>{formatTime(msg.sentAt)}</span>
                        {/* Read status */}
                        {msg.isRead ? (
                          <span className="text-[9px]" style={{ color: colors.primary }}>
                            {tab === 'inbox' ? (i18n.language === 'ko' ? '읽음' : 'Read') : (i18n.language === 'ko' ? '읽음' : 'Read')}
                            {msg.readAt && ` ${formatTime(msg.readAt)}`}
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold" style={{ color: tab === 'inbox' ? colors.error : colors.textMuted }}>
                            {i18n.language === 'ko' ? '안읽음' : 'Unread'}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="truncate text-sm" style={{ color: msg.isRead ? colors.textSecondary : colors.text, fontWeight: msg.isRead ? 400 : 600 }}>{msg.content}</p>
                  </div>
                  {!msg.isRead && tab === 'inbox' && (
                    <div className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colors.primary }} />
                  )}
                </button>
              );
            })
        )}
        {/* Action buttons */}
        <div className="flex gap-2 px-3 py-3">
          <button onClick={() => window.dispatchEvent(new CustomEvent('open-dm-compose', { detail: { recipientId: '', recipientNickname: '' } }))}
            className="flex-1 rounded-lg py-2 text-center text-xs font-bold text-white"
            style={{ backgroundColor: colors.primary }}>
            {t('dm.compose')}
          </button>
          {messages.length > 0 && (<>
            <button onClick={async () => {
              const readIds = messages.filter(m => m.isRead).map(m => m.id);
              if (readIds.length === 0) return;
              if (!confirm(t('common.confirm') + '?')) return;
              await deleteMultipleDMs(readIds);
            }}
              className="flex-1 rounded-lg py-2 text-center text-xs font-semibold"
              style={{ backgroundColor: colors.card, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
              {i18n.language === 'ko' ? '읽은 쪽지 삭제' : 'Del Read'}
            </button>
            <button onClick={async () => {
              if (!confirm(t('common.confirm') + '?')) return;
              await deleteMultipleDMs(messages.map(m => m.id));
            }}
              className="flex-1 rounded-lg py-2 text-center text-xs font-semibold"
              style={{ backgroundColor: `${colors.error}15`, color: colors.error, border: `1px solid ${colors.error}30` }}>
              {i18n.language === 'ko' ? '전체 쪽지 삭제' : 'Del All'}
            </button>
          </>)}
        </div>

        {/* Footer */}
        <div className="py-4 text-center">
          <p className="text-[10px]" style={{ color: colors.textMuted }}>
            &copy; 2025-{new Date().getFullYear()} <a href="https://ourhertz.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{i18n.language === 'ko' ? '우리들의 주파수' : 'OurHertz'}</a>. All rights reserved.
          </p>
        </div>
      </div>

      {/* Detail modal */}
      {selectedDM && (() => {
        const otherUid = tab === 'inbox' ? selectedDM.senderId : selectedDM.recipientId;
        const otherNick = tab === 'inbox' ? selectedDM.senderNickname : selectedDM.recipientNickname;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: colors.overlay }} onClick={() => setSelectedDM(null)}>
            <div className="mx-4 w-full max-w-md rounded-2xl p-5" style={{ backgroundColor: colors.surface }}
              onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 flex items-center justify-between">
                <button onClick={() => { setSelectedDM(null); handleViewProfile(otherUid, otherNick); }}
                  className="font-bold hover:underline" style={{ color: colors.text }}>
                  {tab === 'inbox' ? `${selectedDM.senderNickname} → 나` : `나 → ${selectedDM.recipientNickname}`}
                </button>
                <button onClick={() => setSelectedDM(null)} className="text-lg" style={{ color: colors.textMuted }}>✕</button>
              </div>
              <p className="text-xs" style={{ color: colors.textMuted }}>{formatTime(selectedDM.sentAt)}</p>
              <div className="mt-3 max-h-60 overflow-y-auto rounded-lg p-3" style={{ backgroundColor: colors.card }}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: colors.text }}>{selectedDM.content}</p>
              </div>
              {tab === 'inbox' && (
                <button onClick={() => handleReply(selectedDM)}
                  className="mt-3 w-full rounded-lg py-2.5 text-sm font-bold text-white" style={{ backgroundColor: colors.primary }}>
                  {t('dm.compose')}
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
